import { useEffect, useState } from "react";
import axios from "axios";
import { GripVertical } from "lucide-react";
import config from "../../config";
import { useAuth } from "../../context/AuthContext";

const inputCls = "w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm";

export default function PrescriptionCms({ onMsg }) {
  const { getAuthHeaders } = useAuth();
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState("");

  useEffect(() => {
    axios.get(`${config.API}/admin/cms/prescription`, { headers: getAuthHeaders() })
      .then(({ data }) => setTpl(data.template || data))
      .catch((e) => onMsg?.(e.response?.data?.detail || "Could not load prescription template"));
  }, [getAuthHeaders, onMsg]);

  const patch = (extra) => setTpl((prev) => ({ ...prev, ...extra }));

  const moveBlock = (fromId, toId) => {
    setTpl((prev) => {
      const blocks = [...(prev.blocks || [])];
      const from = blocks.findIndex((b) => b.id === fromId);
      const to = blocks.findIndex((b) => b.id === toId);
      if (from < 0 || to < 0 || from === to) return prev;
      const [moved] = blocks.splice(from, 1);
      blocks.splice(to, 0, moved);
      return { ...prev, blocks };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${config.API}/admin/cms/prescription`, tpl, { headers: getAuthHeaders() });
      setTpl(data.template || data);
      onMsg?.("Prescription template saved — it applies to every doctor immediately.");
    } catch (e) {
      onMsg?.(e.response?.data?.detail || "Could not save prescription template");
    } finally {
      setSaving(false);
    }
  };

  if (!tpl) return <p className="text-sm text-[#673c96]">Loading prescription pad…</p>;

  return (
    <div className="space-y-4" data-testid="prescription-cms">
      <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
        <h2 className="font-semibold text-[#311b52]">Doctor prescription pad</h2>
        <p className="text-xs text-[#6b7280]">There is one pad for the whole clinic. Drag blocks to reorder. Every doctor prescription uses this layout.</p>
        <label className="block text-sm text-[#311b52]">
          <span className="font-medium">Title</span>
          <input className={`${inputCls} mt-1`} value={tpl.title || ""} onChange={(e) => patch({ title: e.target.value })} />
        </label>
        <label className="block text-sm text-[#311b52]">
          <span className="font-medium">Tagline</span>
          <input className={`${inputCls} mt-1`} value={tpl.tagline || ""} onChange={(e) => patch({ tagline: e.target.value })} />
        </label>
        <label className="block text-sm text-[#311b52]">
          <span className="font-medium">Subtitle</span>
          <input className={`${inputCls} mt-1`} value={tpl.subtitle || ""} onChange={(e) => patch({ subtitle: e.target.value })} />
        </label>
        <label className="block text-sm text-[#311b52]">
          <span className="font-medium">Website line</span>
          <input className={`${inputCls} mt-1`} value={tpl.site_url || ""} onChange={(e) => patch({ site_url: e.target.value })} />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={tpl.show_logo !== false} onChange={(e) => patch({ show_logo: e.target.checked })} />
          Show logo on the pad
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={tpl.show_stamp !== false} onChange={(e) => patch({ show_stamp: e.target.checked })} />
          Show BookMyJab verification stamp
        </label>
        <label className="block text-sm text-[#311b52]">
          <span className="font-medium">Footer line 1</span>
          <textarea className={`${inputCls} mt-1`} rows={2} value={tpl.footer_line1 || ""} onChange={(e) => patch({ footer_line1: e.target.value })} />
        </label>
        <label className="block text-sm text-[#311b52]">
          <span className="font-medium">Footer line 2</span>
          <textarea className={`${inputCls} mt-1`} rows={2} value={tpl.footer_line2 || ""} onChange={(e) => patch({ footer_line2: e.target.value })} />
        </label>
      </div>

      <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-2">
        <h2 className="font-semibold text-[#311b52]">Layout (drag to reorder)</h2>
        <p className="text-xs text-[#6b7280] mb-2">Toggle a block off to hide it on every prescription.</p>
        {(tpl.blocks || []).map((block) => (
          <div
            key={block.id}
            draggable
            onDragStart={() => setDragId(block.id)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { if (dragId) moveBlock(dragId, block.id); setDragId(""); }}
            className={`flex items-center gap-3 rounded-lg border px-3 py-2 bg-[#faf8fc] cursor-grab ${dragId === block.id ? "border-[#f37721]" : "border-[#673c96]/15"}`}
            data-testid={`rx-block-${block.id}`}
          >
            <GripVertical className="w-4 h-4 text-[#673c96] shrink-0" />
            <span className="flex-1 text-sm font-medium text-[#311b52]">{block.label || block.id}</span>
            <label className="text-xs flex items-center gap-1">
              <input type="checkbox" checked={block.enabled !== false}
                onChange={(e) => patch({
                  blocks: (tpl.blocks || []).map((b) => b.id === block.id ? { ...b, enabled: e.target.checked } : b),
                })} />
              Visible
            </label>
          </div>
        ))}
      </div>

      <button type="button" disabled={saving} onClick={save}
        className="bg-[#f37721] text-white px-5 py-2.5 rounded-md text-sm font-semibold" data-testid="save-prescription-cms">
        {saving ? "Saving…" : "Save prescription pad"}
      </button>
    </div>
  );
}
